import React from "react";
import styled from "styled-components";
import Fonts from "../../ui/global/Fonts";
import Colors from "../../ui/global/Colors";
import { useCreditsContext, plans } from "./CreditsContext";
import { smartRound } from "../../utils/math";

const UsageRoot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const UsageCard = styled.div<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  min-width: 60vw;
  border-radius: 12px;
  border: 1px solid ${props => props.$colorMode === 'light' ? Colors.grey1 : Colors.grey8};
  overflow: hidden;
  background-color: ${props => props.$colorMode === 'light' ? Colors.white+60 : Colors.grayDark+80};
  line-height: 1.2em;
  padding: 20px;
  margin: 0 10px;
  gap: 10px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    min-width: 82vw;
  }
`;

const UsageRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 20px;
  padding: 4px 0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const UsageGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const ResetContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin: 0 auto;
`;

const UsageHeader = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 20px;
  color: ${props => props.$colorMode === 'light' ? Colors.black : Colors.white};
`;

const SubscriptionBadge = styled.div<{ $colorMode: 'light' | 'dark' }>`
  color: ${props => props.$colorMode === 'dark' ? Colors.grey2 : Colors.grey8};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 40px;
  border: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3};
  padding: 2px 10px;
`;

const UsageLabel = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 16px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey7 : Colors.grey3};
`;

const UsageValue = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 16px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey2};
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProgressBarContainer = styled.div<{ $colorMode: 'light' | 'dark' }>`
  width: 100%;
  height: 8px;
  background-color: ${props => props.$colorMode === 'light' ? Colors.grey2 : Colors.grey8};
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressBarFill = styled.div<{ $percentage: number; $colorMode: 'light' | 'dark' }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background-color: ${props => {
    // if (props.$percentage > 90) return Colors.red;
    // if (props.$percentage > 80) return Colors.yellow;
    return props.$colorMode === 'light' ? Colors.grey9 : Colors.grey1;
  }};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

interface UsageSectionProps {
  colorMode?: 'light' | 'dark';
}

const UsageSection: React.FC<UsageSectionProps> = ({ colorMode = 'dark' }) => {
  const { credits, subscription } = useCreditsContext();
  
  // Calculate percentage of credits used
  const percentageUsed = Math.min(100, Math.round((credits.creditsUsed / credits.totalCredits) * 100) || 0);
  
  // Get the next reset date from credits data or fallback to 1st of next month
  const today = new Date();
  const nextResetDate = credits.nextResetDate ? new Date(credits.nextResetDate) : new Date(today.getFullYear(), today.getMonth() + 1, 1);
  
  // Calculate days until reset
  const daysUntilReset = Math.ceil((nextResetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // Format date with ordinal suffix
  const formatDateWithOrdinal = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    // Add ordinal suffix
    let suffix = 'th';
    if (day % 10 === 1 && day !== 11) suffix = 'st';
    if (day % 10 === 2 && day !== 12) suffix = 'nd';
    if (day % 10 === 3 && day !== 13) suffix = 'rd';
    
    return `${month} ${day}${suffix}, ${year}`;
  };
  
  const resetDate = formatDateWithOrdinal(nextResetDate);

  return (
    <UsageRoot>
      <UsageCard $colorMode={colorMode}>
        <UsageRow>
          <UsageHeader $colorMode={colorMode}>Credits</UsageHeader>
          <SubscriptionBadge $colorMode={colorMode}>{plans.find(p => p.key === subscription)?.name}</SubscriptionBadge>
        </UsageRow>

        <ProgressSection>
        <ProgressBarContainer $colorMode={colorMode}>
          <ProgressBarFill $percentage={percentageUsed} $colorMode={colorMode} />
        </ProgressBarContainer>
        
        <UsageRow>
          <UsageGroup>
            <UsageValue $colorMode={colorMode}>{smartRound(credits.creditsUsed, 1)}</UsageValue>
            <UsageLabel $colorMode={colorMode}> / </UsageLabel>
            <UsageValue $colorMode={colorMode}>{smartRound(credits.totalCredits, 0)}</UsageValue>
            <UsageLabel $colorMode={colorMode}> used</UsageLabel>
          </UsageGroup>
          <UsageGroup>
            <UsageValue $colorMode={colorMode}>{smartRound(credits.creditsRemaining, 1)}</UsageValue>
            <UsageLabel $colorMode={colorMode}> left</UsageLabel>
          </UsageGroup>
        </UsageRow>
        </ProgressSection>
      </UsageCard>

      <UsageRow>
          <ResetContainer>
            <UsageLabel $colorMode={colorMode}>Monthly refresh</UsageLabel>
            <UsageLabel $colorMode={colorMode}>in </UsageLabel>
            <UsageValue $colorMode={colorMode}>{daysUntilReset} days</UsageValue>
            <UsageLabel $colorMode={colorMode}>on {resetDate}</UsageLabel>
          </ResetContainer>
        </UsageRow>
    </UsageRoot>
  );

};

export default UsageSection;